import { Suspense, lazy, type ReactNode } from 'react'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import { AppLayout } from './shell/AppLayout'
import { ModuleEntry } from './modules/ModuleEntry'
import { ProtectedRoute } from '@/shared/components/ProtectedRoute'
import { NotFoundPage } from '@/shared/components/NotFoundPage'
import { RouteErrorBoundary } from '@/shared/components/RouteErrorBoundary'
import { RouteFallback } from '@/shared/components/RouteFallback'

// Route pages are lazy so the entry chunk carries the shell, not every screen —
// in particular, someone who never signs in should not download the workspace,
// the settings and the status page just to see the login form (request 0194).
const LoginPage = lazy(() =>
  import('@/features/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
)
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
)
const WorkspacePage = lazy(() =>
  import('@/features/workspace/WorkspacePage').then((m) => ({ default: m.WorkspacePage })),
)
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const StatusPage = lazy(() =>
  import('@/features/status/StatusPage').then((m) => ({ default: m.StatusPage })),
)
const NotificationsPage = lazy(() =>
  import('@/features/notifications/NotificationsPage').then((m) => ({
    default: m.NotificationsPage,
  })),
)

/** Every lazy route element needs its own boundary — `ModuleEntry` brings its own. */
function suspended(element: ReactNode): ReactNode {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>
}

// `/new-user`: campaign registration (?cid) is deferred to Phase 5/7; first-access
// password setup reuses the reset-password flow in `new-user` mode.
function NewUserRoute() {
  return <ResetPasswordPage variant="new-user" />
}

const contentError = <RouteErrorBoundary fit="content" />
const pageError = <RouteErrorBoundary fit="page" />

const router = createBrowserRouter([
  { path: '/login', element: suspended(<LoginPage />), errorElement: pageError },
  { path: '/reset-password', element: suspended(<ResetPasswordPage />), errorElement: pageError },
  { path: '/new-user', element: suspended(<NewUserRoute />), errorElement: pageError },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: pageError,
    children: [
      { index: true, element: suspended(<WorkspacePage />), errorElement: contentError },
      { path: 'settings', element: suspended(<SettingsPage />), errorElement: contentError },
      { path: 'status', element: suspended(<StatusPage />), errorElement: contentError },
      {
        path: 'notifications',
        element: suspended(<NotificationsPage />),
        errorElement: contentError,
      },
      { path: 'm/:slug', element: <ModuleEntry />, errorElement: contentError },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
